import { Inject, Injectable } from '@angular/core';
import { DynamoDBService } from './ddb.service';
import { CognitoCallback, CognitoUtil, LoggedInCallback } from './cognito.service';
import { AuthenticationDetails, CognitoUser } from 'amazon-cognito-identity-js';
import * as AWS from 'aws-sdk/global';
import * as STS from 'aws-sdk/clients/sts';
import { Observable } from 'rxjs';
import { AnalyticsService } from './analytics.service';

@Injectable()
export class UserLoginService {

    constructor(
        public ddb: DynamoDBService,
        public cognitoUtil: CognitoUtil,
        private readonly analyticsService: AnalyticsService) { }

    user: string;
    pw: string;

    authenticate(username: string, password: string, callback: CognitoCallback) {
        const authenticationData = {
            Username: username,
            Password: password,
        };

        this.user = username;
        this.pw = password;

        const authenticationDetails = new AuthenticationDetails(authenticationData);

        const userData = {
            Username: username,
            Pool: this.cognitoUtil.getUserPool()
        };

        const cognitoUser = new CognitoUser(userData);
        cognitoUser.authenticateUser(authenticationDetails, {
            newPasswordRequired: (userAttributes, requiredAttributes) =>
                callback.cognitoCallback(`User needs to set password.`, null),
            onSuccess: result => {
                const creds = this.cognitoUtil.buildCognitoCreds(result.getIdToken().getJwtToken());

                //Pull Sub for Cognito User Object
                const idToken = result.getIdToken();
                const accessToken = result.getAccessToken();
                const sub = idToken["payload"].sub;

                this.analyticsService.identify(sub);

                AWS.config.region = 'us-west-2';
                AWS.config.credentials = creds;
 
                // So, when CognitoIdentity authenticates a user, it doesn't actually hand us the IdentityID,
                // used by many of our other handlers. This is handled by some sly underhanded calls to AWS Cognito
                // API's by the SDK itself, automatically when the first AWS SDK request is made that requires our
                // security credentials. The identity is then injected directly into the credentials object.
                // If the first SDK call we make wants to use our IdentityID, we have a
                // chicken and egg problem on our hands. We resolve this problem by "priming" the AWS SDK by calling a
                // very innocuous API call that forces this behavior.
                const sts = new STS();
                try {
                    sts.getCallerIdentity((err, data) => {
                        localStorage.setItem('AWSCreds', JSON.stringify(creds.params));
                        localStorage.setItem('AccessToken', accessToken.getJwtToken());
                        localStorage.setItem('CognitoId', creds.identityId);
                        localStorage.setItem('CognitoSub', sub);
                        callback.cognitoCallback(null, result);
                    });
                } catch (e) {
                    console.log(e);
                }
            },
            onFailure: err => {
                callback.cognitoCallback(err.message, null);
                return true;
            },
            customChallenge: function(challengeParameters) {
                // Trigger invisible reCaptcha
                grecaptcha.execute();
                // Get data from reCaptcha and pass back in sendCustomChallengeAnswer
                cognitoUser.sendCustomChallengeAnswer(
                    grecaptcha.getResponse(),
                    // The `this` here is not referring to the component but rather the event handler object of which customChallenge is a member.
                    this);
            }
        });
    }

    forgotPassword(username: string, callback: CognitoCallback) {
        const userData = {
            Username: username,
            Pool: this.cognitoUtil.getUserPool()
        };

        const cognitoUser = new CognitoUser(userData);

        cognitoUser.forgotPassword({
            onSuccess: function () {

            },
            onFailure: function (err) {
                callback.cognitoCallback(err.message, null);
            },
            inputVerificationCode() {
                callback.cognitoCallback(null, null);
            }
        });
    }

    confirmNewPassword(email: string, verificationCode: string, password: string, callback: CognitoCallback) {
        const userData = {
            Username: email,
            Pool: this.cognitoUtil.getUserPool()
        };

        const cognitoUser = new CognitoUser(userData);

        cognitoUser.confirmPassword(verificationCode, password, {
            onSuccess: function () {
                callback.cognitoCallback(null, null);
            },
            onFailure: function (err) {
                callback.cognitoCallback(err.message, null);
            }
        });
    }

    logout() {
        const currUser = this.cognitoUtil.getCurrentUser();
        if (currUser) {
            this.cognitoUtil.getCurrentUser().signOut();
        }
    }
    

    isAuthenticated(callback: LoggedInCallback) {
        if (callback == null) {
            throw('UserLoginService: Callback in isAuthenticated() cannot be null');
        }

        const cognitoUser = this.cognitoUtil.getCurrentUser();

        if (cognitoUser != null) {
            cognitoUser.getSession(function (err, session) {
                if (err) {
                    callback.isLoggedIn(err, false);
                } else {
                    callback.isLoggedIn(err, session.isValid());
                }
            });
        } else {
            callback.isLoggedIn('Can\'t retrieve the CurrentUser', false);
        }
    }

    isLoggedIn(cognitoUtil) {

      const cognitoUser = cognitoUtil.getCurrentUser();

      if (cognitoUser != null) {
        return cognitoUser.getSession(function (err, session) {
          return err ? false : session.isValid();
        });
      } else {
        return false;
      }
    }
}
